import { useSession } from 'src/providers/auth.provider'
import { useLocalizedNavigate } from 'src/hooks/use-localized-navigate'
import { AxiosError } from 'axios'
import * as Sentry from '@sentry/react'
import { PermissionError } from 'src/types/errors'
import type { TRequiredPermission } from 'src/pages/configuration/permissions/_components/permissions.type'
import { EPermissionSpecialAuthorization } from 'src/pages/configuration/permissions/_components/permissions.type'

type ErrorResponseData = {
  requiredPermission?: string
  requiredSpecialAuthorization?: string
  message?: string
}

type ErrorQueryHandlerProps = {
  error?: AxiosError<ErrorResponseData> | Error | unknown
}

const ErrorQueryHandler = (props: ErrorQueryHandlerProps) => {
  const error = props?.error as AxiosError<ErrorResponseData> | undefined
  const { logout, currentUser } = useSession()
  const navigate = useLocalizedNavigate()

  if (error?.response?.status === 401) {
    logout && logout({ isAutomatic: true })
  } else if (error?.response?.status === 403 && error?.response?.data?.requiredPermission) {
    Sentry.captureException(error, {
      user: { id: currentUser?.id },
    })

    throw new PermissionError({
      requiredPermission: error.response.data.requiredPermission as unknown as TRequiredPermission,
    })
  } else if (error?.response?.status === 403 && error?.response?.data?.requiredSpecialAuthorization) {
    Sentry.captureException(error, {
      user: { id: currentUser?.id },
    })

    throw new PermissionError({
      requiredSpecialAuthorization: error?.response?.data
        ?.requiredSpecialAuthorization as EPermissionSpecialAuthorization,
    })
  } else if (error?.response?.status === 403 && error?.response?.data?.message === 'NETWORK_ACCESS_DENIED') {
    throw new PermissionError({ isNetworkBlocked: true })
  } else if (error?.response?.status === 403) {
    Sentry.captureException(error, {
      user: { id: currentUser?.id },
    })
    navigate('/')
  } else {
    Sentry.captureException(error, {
      user: { id: currentUser?.id },
    })

    throw error instanceof Error ? error : new Error(String(error))
  }

  return null
}

export { ErrorQueryHandler }
